import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import {
  CompanyGroupFormDto,
  type CompanyGroupFormInterface
} from '@zaoblako/shared/modules/client/dtos/company.group/company.group.form.dto';
import type { CompanyGroupFindInterface } from '@zaoblako/shared/modules/client/dtos/company.group/company-group-find.dto';
import { CompanyGroupService } from '@zaoblako/web/core/services/client/company-group.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { RoleService } from '@zaoblako/web/core/services/security/role.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { CompanyMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/company-multi-select/company-multi-select.component';
import { CompanySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/company-single-select/company-single-select.component';
import { StandardMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/standard-dropdown/standard-multi-select/standard-multi-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-company-group-form',
  templateUrl: './company-group-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    ButtonComponent,
    SpinnerComponent,
    CompanySingleSelectComponent,
    CompanyMultiSelectComponent,
    StandardMultiSelectComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompanyGroupFormComponent extends BaseFormComponent<CompanyGroupFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public group = <CompanyGroupFormInterface>{};
  public dto = CompanyGroupFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public readonly roleDropdownDescriptorUrl: string;

  public constructor(
    roleService: RoleService,
    private readonly companyGroupService: CompanyGroupService,
    private readonly formStoreService: FormStoreService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService
  ) {
    super();
    this.roleDropdownDescriptorUrl = roleService.dropdownDescriptorUrl;
  }

  public ngOnInit() {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.companyGroupService
      .findById<CompanyGroupFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (group) => {
          this.group = group as any;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveGroup() {
    if (this.checkValidate(this.group).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.companyGroupService.update<CompanyGroupFormInterface, CompanyGroupFindInterface>(this.group).subscribe({
          next: (group) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Группа "${this.group?.name}" обновлена`);
            this.formStoreService.saveNext(group, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.companyGroupService.create<CompanyGroupFindInterface>(this.group).subscribe({
          next: (group) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Группа компаний создана');
            this.formStoreService.saveNext(group, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteGroup() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить группу компаний "${this.group?.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.companyGroupService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Группа "${this.group?.name}" удалена`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }
}
